/**
 * NewsCurateModal — Editorial modal for community news curation.
 *
 * Tab 1 — Sources: News API config, Article Pool, Life4Earth content
 * Tab 2 — Curate: pending review queue + approved articles with
 *   placement controls (featured / card / headline).
 * Tab 3 — Preview & Publish: visual preview with drag-and-drop reorder.
 *
 * Only accessible to community owners, admins, and system admins.
 * All user-facing strings use i18n via useAppTranslation('pages').
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import CloseButton from '../CloseButton/CloseButton';
import {
  fetchSubscription,
  upsertSubscription,
  fetchMasterSources,
  fetchPendingItems,
  fetchNewsFeed,
  fetchArticlesIntoQueue,
  fetchCommunityNewsAccess,
  moderateItems,
  updateArticlePlacement,
  updateSortOrder,
  fetchArticlePool,
  adoptPoolArticles,
  NewsSubscription,
  NewsSource,
  NewsModerationItem,
  ModerationAction,
  ArticlePlacement,
  PoolArticle,
} from '../../api/newsApi';
import { previewUrl, shareToCommunityFeed, type UrlPreview } from '../../api/feedShare';
import { uploadFile, fileUrl } from '../../api/fileStorage';
import { mediaSrc } from '../../api/mediaUrl';
import styles from './NewsCurateModal.module.css';
import { sanitizeEmbed } from '../../utils/sanitizeHtml';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface Props {
  communityGuid: string;
  communityName: string;
  isOpen: boolean;
  onClose: () => void;
  onChanged?: () => void; // refresh feed after curation
}

type ModalTab = 'sources' | 'curate' | 'preview';
type SourcesSubTab = 'newsapi' | 'pool' | 'l4e' | 'web';

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function formatDate(dateStr?: string): string {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleDateString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  } catch { return dateStr; }
}

function contentTypeBadge(type: string) {
  switch (type) {
    case 'api_article': return { label: 'API', cls: styles.badgeApi };
    case 'user_blog': return { label: 'Blog', cls: styles.badgeBlog };
    case 'community_share': return { label: 'Share', cls: styles.badgeShare };
    default: return { label: type, cls: styles.badgeApi };
  }
}

/** Filter out auto-generated feed companion records (news_featured).
 *  The curate modal should only manage editorial content types. */
function excludeFeedCompanions(items: NewsModerationItem[]): NewsModerationItem[] {
  return items.filter((i) => i.contentType !== 'news_featured');
}

/** Sort by sort_order (nulls last), then by date descending. */
function sortBySortOrder(a: NewsModerationItem, b: NewsModerationItem): number {
  const sa = a.sortOrder ?? Number.MAX_SAFE_INTEGER;
  const sb = b.sortOrder ?? Number.MAX_SAFE_INTEGER;
  if (sa !== sb) return sa - sb;
  const da = a.article?.publishedAt ? new Date(a.article.publishedAt).getTime() : 0;
  const db = b.article?.publishedAt ? new Date(b.article.publishedAt).getTime() : 0;
  return db - da;
}

/** Split approved items into hero / cards / headlines using placement or auto-assign. */
function computePreviewLayout(items: NewsModerationItem[]) {
  const featured = items.filter((i) => i.placement === 'featured' || (!i.placement && i.isFeatured));
  const cards = items.filter((i) => i.placement === 'card').sort(sortBySortOrder);
  const headlines = items.filter((i) => i.placement === 'headline').sort(sortBySortOrder);
  const unassigned = items.filter((i) => !i.placement && !i.isFeatured);

  // Hero: first featured, or first unassigned
  let hero = featured[0] ?? null;
  const extraFeatured = featured.slice(1);

  if (!hero && unassigned.length > 0) {
    hero = unassigned.shift()!;
  }

  // Default: unassigned and extra featured go to cards (not headlines)
  const autoCards = [...cards, ...extraFeatured, ...unassigned];
  const autoHeadlines = [...headlines];

  return { hero, cards: autoCards, headlines: autoHeadlines };
}

function timeSince(dateStr?: string): string {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffH = Math.floor(diffMs / 3600000);
    if (diffH < 1) return 'Just now';
    if (diffH < 24) return `${diffH}h ago`;
    const diffD = Math.floor(diffH / 24);
    if (diffD < 7) return `${diffD}d ago`;
    return formatDate(dateStr);
  } catch {
    return '';
  }
}

/** Assign a pool article to a time-based bucket for grouped display. */
type TimeBucket = 'lastHour' | 'lastDay' | 'last3Days' | 'lastWeek' | 'lastMonth' | 'older';

const TIME_BUCKET_ORDER: TimeBucket[] = ['lastHour', 'lastDay', 'last3Days', 'lastWeek', 'lastMonth', 'older'];

function getTimeBucket(dateStr?: string): TimeBucket {
  if (!dateStr) return 'older';
  try {
    const now = Date.now();
    const pub = new Date(dateStr).getTime();
    const diffMs = now - pub;
    const HOUR = 3_600_000;
    const DAY = 86_400_000;
    if (diffMs < HOUR) return 'lastHour';
    if (diffMs < DAY) return 'lastDay';
    if (diffMs < 3 * DAY) return 'last3Days';
    if (diffMs < 7 * DAY) return 'lastWeek';
    if (diffMs < 30 * DAY) return 'lastMonth';
    return 'older';
  } catch {
    return 'older';
  }
}

function groupByTimeBucket(articles: PoolArticle[]): Map<TimeBucket, PoolArticle[]> {
  const map = new Map<TimeBucket, PoolArticle[]>();
  for (const bucket of TIME_BUCKET_ORDER) map.set(bucket, []);
  for (const article of articles) {
    const bucket = getTimeBucket(article.publishedAt);
    map.get(bucket)!.push(article);
  }
  return map;
}

/** EventRegistry DMOZ category URIs available for exclusion. */
const CATEGORY_OPTIONS = [
  { uri: 'dmoz/Sports', labelKey: 'catSports' },
  { uri: 'dmoz/Games', labelKey: 'catGames' },
  { uri: 'dmoz/Society/Religion_and_Spirituality', labelKey: 'catReligion' },
  { uri: 'dmoz/Adult', labelKey: 'catAdult' },
  { uri: 'dmoz/Recreation', labelKey: 'catRecreation' },
  { uri: 'dmoz/Arts/Entertainment', labelKey: 'catEntertainment' },
  { uri: 'dmoz/Shopping', labelKey: 'catShopping' },
  { uri: 'dmoz/Computers/Internet/On_the_Web/Online_Communities', labelKey: 'catSocialMedia' },
] as const;

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

const NewsCurateModal: React.FC<Props> = ({
  communityGuid, communityName, isOpen, onClose, onChanged,
}) => {
  const { t } = useAppTranslation('pages');

  // ── Shared state ────────────────────────────────────────
  const [tab, setTab] = useState<ModalTab>('curate');
  const [sourcesSubTab, setSourcesSubTab] = useState<SourcesSubTab>('newsapi');
  const [apiEnabled, setApiEnabled] = useState(false);

  // ── Configure tab state ─────────────────────────────────
  const [subscription, setSubscription] = useState<NewsSubscription | null>(null);
  const [masterSources, setMasterSources] = useState<NewsSource[]>([]);
  const [keywords, setKeywords] = useState<string[]>([]);
  const [newKeyword, setNewKeyword] = useState('');
  const [selectedSources, setSelectedSources] = useState<string[] | null>(null);
  const [excludedCategories, setExcludedCategories] = useState<string[]>([]);
  const [moderationMode, setModerationMode] = useState<'queue' | 'auto_approve'>('queue');
  const [maxArticles, setMaxArticles] = useState(15);
  const [configLoading, setConfigLoading] = useState(true);
  const [configSaving, setConfigSaving] = useState(false);
  const [configDirty, setConfigDirty] = useState(false);
  const [fetchingNews, setFetchingNews] = useState(false);
  const [fetchResult, setFetchResult] = useState<string | null>(null);
  const [sourceFilter, setSourceFilter] = useState('');

  // ── Curate tab state ────────────────────────────────────
  const [curateSearch, setCurateSearch] = useState('');
  const [pendingItems, setPendingItems] = useState<NewsModerationItem[]>([]);
  const [approvedItems, setApprovedItems] = useState<NewsModerationItem[]>([]);
  const [curateLoading, setCurateLoading] = useState(true);
  const [acting, setActing] = useState<string | null>(null);

  // ── Article Pool tab state ─────────────────────────────
  const [poolArticles, setPoolArticles] = useState<PoolArticle[]>([]);
  const [poolTotal, setPoolTotal] = useState(0);
  const [poolPage, setPoolPage] = useState(1);
  const [poolSearch, setPoolSearch] = useState('');
  const [poolLoading, setPoolLoading] = useState(false);
  const [poolSelected, setPoolSelected] = useState<Set<string>>(new Set());
  const [poolAdopting, setPoolAdopting] = useState(false);
  const [poolCollapsed, setPoolCollapsed] = useState<Set<TimeBucket>>(new Set());

  // ── L4E Sources tab state ─────────────────────────────
  const [l4eItems, setL4eItems] = useState<NewsModerationItem[]>([]);
  const [l4eLoading, setL4eLoading] = useState(false);
  const [l4eSelected, setL4eSelected] = useState<Set<string>>(new Set());
  // l4eAdopting removed — L4E items are already approved; no adopt call needed

  // ── Web URL source tab state ─────────────────────────
  const [webItems, setWebItems] = useState<NewsModerationItem[]>([]);
  const [webItemsLoading, setWebItemsLoading] = useState(false);
  const [webSelected, setWebSelected] = useState<Set<string>>(new Set());
  const [webAddOpen, setWebAddOpen] = useState(false); // inline "Add Web Link" modal
  const [webUrl, setWebUrl] = useState('');
  const [webComment, setWebComment] = useState('');
  // DEV-966: curator-editable intro text. Auto-fills from the server's
  // extracted description when a preview arrives; the curator can edit
  // or replace with their own summary. Submitted as excerptOverride.
  const [webDescription, setWebDescription] = useState('');
  // DEV-967: curator-editable title. Same pattern — auto-fills from the
  // server's extracted title; sent as titleOverride only when different.
  const [webTitle, setWebTitle] = useState('');
  const [webPreview, setWebPreview] = useState<UrlPreview | null>(null);
  const [webPreviewLoading, setWebPreviewLoading] = useState(false);
  const [webPreviewError, setWebPreviewError] = useState(false);
  const [webSubmitting, setWebSubmitting] = useState(false);
  const [webSuccess, setWebSuccess] = useState(false);
  const [webError, setWebError] = useState<string | null>(null);
  const [webImageIndex, setWebImageIndex] = useState(0);
  // ── Key photo upload state (web link tab) ──────────
  const [webUploadedPhoto, setWebUploadedPhoto] = useState<File | null>(null);
  const [webUploadedPhotoUrl, setWebUploadedPhotoUrl] = useState<string>('');
  const [webPhotoCredit, setWebPhotoCredit] = useState('');
  const [webPhotoRightsAck, setWebPhotoRightsAck] = useState(false);
  const webPhotoInputRef = useRef<HTMLInputElement>(null);
  const webDebounceRef = useRef<ReturnType<typeof setTimeout>>();

  const poolBuckets = useMemo(() => groupByTimeBucket(poolArticles), [poolArticles]);

  const expandPoolBucket = useCallback((bucket: TimeBucket) => {
    setPoolCollapsed((prev) => {
      if (!prev.has(bucket)) return prev;
      const next = new Set(prev);
      next.delete(bucket);
      return next;
    });
  }, []);

  const collapsePoolBucket = useCallback((bucket: TimeBucket) => {
    setPoolCollapsed((prev) => {
      if (prev.has(bucket)) return prev;
      const next = new Set(prev);
      next.add(bucket);
      return next;
    });
  }, []);

  // ── Preview tab: ordered layout with reorder support ────
  const [previewCards, setPreviewCards] = useState<NewsModerationItem[]>([]);
  const [previewHeadlines, setPreviewHeadlines] = useState<NewsModerationItem[]>([]);
  const [previewHero, setPreviewHero] = useState<NewsModerationItem | null>(null);

  // Rebuild preview order whenever approved items change or user switches to preview
  useEffect(() => {
    if (tab !== 'preview') return;
    const layout = computePreviewLayout(approvedItems);
    setPreviewHero(layout.hero);
    setPreviewCards(layout.cards);
    setPreviewHeadlines(layout.headlines);
  }, [tab, approvedItems]);

  // ── Drag-and-drop reorder state ────────────────────────
  const dragIndexRef = useRef<number | null>(null);
  const dragListRef = useRef<'cards' | 'headlines' | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);
  const [draggingList, setDraggingList] = useState<'cards' | 'headlines' | null>(null);

  /** Persist sort_order for all items in a list after reorder. */
  const persistSortOrder = useCallback((items: NewsModerationItem[]) => {
    const payload = items.map((item, idx) => ({
      moderationGuid: item.moderationGuid,
      sortOrder: idx,
    }));
    updateSortOrder(payload).catch(() => {
      // Silent — order still reflected locally
    });
  }, []);

  const handleDragStart = useCallback((e: React.DragEvent, listName: 'cards' | 'headlines', index: number) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(index));
    dragIndexRef.current = index;
    dragListRef.current = listName;
    setDraggingIndex(index);
    setDraggingList(listName);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, listName: 'cards' | 'headlines', index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragListRef.current !== listName) return;
    setDragOverIndex(index);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent, listName: 'cards' | 'headlines', dropIndex: number) => {
    e.preventDefault();
    const srcIndex = dragIndexRef.current;
    if (srcIndex === null || dragListRef.current !== listName || srcIndex === dropIndex) {
      setDragOverIndex(null);
      setDraggingIndex(null);
      setDraggingList(null);
      return;
    }
    const setter = listName === 'cards' ? setPreviewCards : setPreviewHeadlines;
    setter((prev) => {
      const next = [...prev];
      const [moved] = next.splice(srcIndex, 1);
      next.splice(dropIndex, 0, moved);
      // Persist order to backend
      persistSortOrder(next);
      return next;
    });
    dragIndexRef.current = null;
    dragListRef.current = null;
    setDragOverIndex(null);
    setDraggingIndex(null);
    setDraggingList(null);
  }, [persistSortOrder]);

  const handleDragEnd = useCallback(() => {
    dragIndexRef.current = null;
    dragListRef.current = null;
    setDragOverIndex(null);
    setDraggingIndex(null);
    setDraggingList(null);
  }, []);

  // ── Load data on open ───────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;

    (async () => {
      // Check community API access
      try {
        const accessList = await fetchCommunityNewsAccess();
        if (cancelled) return;
        const match = accessList.find(
          (a) => a.communityGuid.toUpperCase() === communityGuid.toUpperCase()
        );
        const enabled = match?.newsEnabled ?? false;
        setApiEnabled(enabled);
        // Default to Sources tab with appropriate subtab
        setTab(enabled ? 'sources' : 'curate');
        setSourcesSubTab(enabled ? 'newsapi' : 'pool');
      } catch {
        setApiEnabled(false);
        setTab('curate');
      }

      // Load configure data
      setConfigLoading(true);
      try {
        const [sub, sources] = await Promise.all([
          fetchSubscription(communityGuid),
          fetchMasterSources(),
        ]);
        if (cancelled) return;
        setSubscription(sub);
        setMasterSources(sources.filter((s) => s.isEnabled));
        if (sub) {
          setKeywords(sub.keywords || []);
          setSelectedSources(sub.allowedSourceUris);
          setExcludedCategories(sub.excludedCategories || []);
          setModerationMode(sub.moderationMode || 'queue');
          setMaxArticles(sub.maxArticles || 15);
        } else {
          // Defaults for new subscription — exclude sports by default
          setKeywords([communityName]);
          setSelectedSources(null); // null = all sources
          setExcludedCategories(['dmoz/Sports', 'dmoz/Games', 'dmoz/Adult']);
          setModerationMode('queue');
          setMaxArticles(15);
        }
        setConfigDirty(false);
      } catch {
        // Failed to load config
      } finally {
        if (!cancelled) setConfigLoading(false);
      }

      // Load curate data
      setCurateLoading(true);
      try {
        const [pending, approvedRaw] = await Promise.all([
          fetchPendingItems(communityGuid),
          fetchNewsFeed(communityGuid, { count: 50 }),
        ]);
        if (cancelled) return;
        setPendingItems(pending);
        setApprovedItems(excludeFeedCompanions(approvedRaw));
      } catch {
        // Endpoints may not be ready yet
      } finally {
        if (!cancelled) setCurateLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [isOpen, communityGuid, communityName]);

  // ── Load pool data when tab is selected ─────────────────
  const loadPool = useCallback(async (pg = 1, search = '') => {
    setPoolLoading(true);
    try {
      const result = await fetchArticlePool(communityGuid, { search: search || undefined, page: pg, count: 20 });
      setPoolArticles(result.articles);
      setPoolTotal(result.total);
      setPoolPage(pg);
    } catch {
      // Pool endpoint may not be ready
    } finally {
      setPoolLoading(false);
    }
  }, [communityGuid]);

  useEffect(() => {
    if (isOpen && tab === 'sources' && sourcesSubTab === 'pool') {
      loadPool(1, poolSearch);
      setPoolSelected(new Set());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, tab, sourcesSubTab]);

  // ── Pool: adopt selected articles ─────────────────────
  const handleAdopt = useCallback(async () => {
    if (poolSelected.size === 0) return;
    setPoolAdopting(true);
    try {
      const result = await adoptPoolArticles(communityGuid, Array.from(poolSelected));
      // Reload pending items and pool
      const pending = await fetchPendingItems(communityGuid);
      setPendingItems(pending);
      setPoolSelected(new Set());
      // Remove adopted articles from the current pool view
      setPoolArticles((prev) => prev.filter((a) => !poolSelected.has(a.newsItemGuid)));
      setPoolTotal((prev) => prev - result.adopted);
    } catch {
      // Fail silently
    } finally {
      setPoolAdopting(false);
    }
  }, [communityGuid, poolSelected]);

  const togglePoolSelection = useCallback((guid: string) => {
    setPoolSelected((prev) => {
      const next = new Set(prev);
      if (next.has(guid)) next.delete(guid);
      else next.add(guid);
      return next;
    });
  }, []);

  // ── L4E Sources: load platform content (chronicles / blogs only) ──
  const loadL4eItems = useCallback(async () => {
    setL4eLoading(true);
    try {
      const feed = await fetchNewsFeed(communityGuid, { count: 50, contentType: 'community_share' });
      // Only keep items WITH a chronicleGuid (platform content).
      // External web links (no chronicleGuid) now live on the Web tab.
      const seen = new Set<string>();
      const platformOnly = feed.filter((item) => {
        const cg = item.article?.chronicleGuid;
        if (!cg) return false; // external web link — belongs on Web tab
        if (seen.has(cg)) return false; // duplicate chronicle
        seen.add(cg);
        return true;
      });
      setL4eItems(platformOnly);
    } catch {
      setL4eItems([]);
    } finally {
      setL4eLoading(false);
    }
  }, [communityGuid]);

  useEffect(() => {
    if (isOpen && tab === 'sources' && sourcesSubTab === 'l4e') {
      loadL4eItems();
      setL4eSelected(new Set());
    }
  }, [isOpen, tab, sourcesSubTab, loadL4eItems]);

  // ── Web Sources: load external web links (no chronicleGuid) ──
  const loadWebItems = useCallback(async () => {
    setWebItemsLoading(true);
    try {
      const feed = await fetchNewsFeed(communityGuid, { count: 50, contentType: 'community_share' });
      // Only keep items WITHOUT chronicleGuid — these are external web links.
      const webOnly = feed.filter((item) => !item.article?.chronicleGuid);
      setWebItems(webOnly);
    } catch {
      setWebItems([]);
    } finally {
      setWebItemsLoading(false);
    }
  }, [communityGuid]);

  useEffect(() => {
    if (isOpen && tab === 'sources' && sourcesSubTab === 'web') {
      loadWebItems();
      setWebSelected(new Set());
    }
  }, [isOpen, tab, sourcesSubTab, loadWebItems]);

  const toggleWebSelection = useCallback((guid: string) => {
    setWebSelected((prev) => {
      const next = new Set(prev);
      if (next.has(guid)) next.delete(guid);
      else next.add(guid);
      return next;
    });
  }, []);

  /** Remove a web article — rejects the moderation record, removes from local state,
   *  and refreshes approved items from the server (since the backend may auto-promote
   *  the next card to featured if the rejected article was the featured article). */
  const handleRemoveWebItem = useCallback(async (moderationGuid: string) => {
    setActing(moderationGuid);
    try {
      await moderateItems([{ moderationGuid, action: 'reject' }]);
      setWebItems((prev) => prev.filter((i) => i.moderationGuid !== moderationGuid));
      // Refresh approved items from the server so any auto-promotion is reflected
      try {
        const approvedRaw = await fetchNewsFeed(communityGuid, { count: 50 });
        setApprovedItems(excludeFeedCompanions(approvedRaw));
      } catch {
        // Fall back to local removal if refresh fails
        setApprovedItems((prev) => prev.filter((i) => i.moderationGuid !== moderationGuid));
      }
    } catch {
      // Fail silently
    } finally {
      setActing(null);
    }
  }, [communityGuid]);

  const toggleL4eSelection = useCallback((guid: string) => {
    setL4eSelected((prev) => {
      const next = new Set(prev);
      if (next.has(guid)) next.delete(guid);
      else next.add(guid);
      return next;
    });
  }, []);

  /** Navigate to the Curate tab to manage selected L4E items.
   *  Community shares are auto-approved at share time, so they're already
   *  in the curate queue's approved section.  Refresh curate data to ensure
   *  it reflects any changes made since the modal opened. */
  const handleViewInCurate = useCallback(async () => {
    setL4eSelected(new Set());
    setTab('curate');
    // Refresh curate data so the chronicle is guaranteed visible
    try {
      const [pending, approvedRaw] = await Promise.all([
        fetchPendingItems(communityGuid),
        fetchNewsFeed(communityGuid, { count: 50 }),
      ]);
      setPendingItems(pending);
      setApprovedItems(excludeFeedCompanions(approvedRaw));
    } catch {
      // Non-fatal — curate tab will show whatever was last loaded
    }
  }, [communityGuid]);

  // ── Web URL: preview debounce ──────────────────────────
  useEffect(() => {
    if (sourcesSubTab !== 'web') return;
    if (!webUrl.trim() || webUrl.trim().length < 10) {
      setWebPreview(null);
      setWebPreviewError(false);
      return;
    }

    clearTimeout(webDebounceRef.current);
    webDebounceRef.current = setTimeout(async () => {
      setWebPreviewLoading(true);
      setWebPreviewError(false);
      try {
        const p = await previewUrl(webUrl.trim());
        setWebPreview(p);
        setWebImageIndex(0);
        // DEV-966: seed the intro textarea with whatever the server scraped.
        // Curator can edit; only seed when the textarea is empty (don't
        // clobber their in-progress edits if the preview re-fetches).
        setWebDescription((prev) => (prev.trim() ? prev : (p.description ?? '')));
        // DEV-967: same pattern for title.
        setWebTitle((prev) => (prev.trim() ? prev : (p.title ?? '')));
        setWebPreviewError(!p.title);
      } catch {
        setWebPreviewError(true);
        setWebPreview(null);
      } finally {
        setWebPreviewLoading(false);
      }
    }, 600);

    return () => clearTimeout(webDebounceRef.current);
  }, [webUrl, sourcesSubTab]);

  // ── Web URL: reset add-modal state on tab switch ─────
  useEffect(() => {
    if (sourcesSubTab !== 'web') return;
    setWebAddOpen(false);
    setWebUrl('');
    setWebComment('');
    setWebPreview(null);
    setWebPreviewLoading(false);
    setWebPreviewError(false);
    setWebSubmitting(false);
    setWebSuccess(false);
    setWebError(null);
    // Reset key photo upload state
    setWebUploadedPhoto(null);
    setWebUploadedPhotoUrl('');
    setWebPhotoCredit('');
    setWebPhotoRightsAck(false);
  }, [sourcesSubTab]);

  /** Handle key photo file selection (web link tab) */
  const handleWebPhotoSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Validate type
    if (!file.type.startsWith('image/')) return;
    // Validate size (5 MB max)
    if (file.size > 5 * 1024 * 1024) return;
    setWebUploadedPhoto(file);
    setWebUploadedPhotoUrl(URL.createObjectURL(file));
  }, []);

  /** Remove the uploaded key photo */
  const handleWebPhotoRemove = useCallback(() => {
    if (webUploadedPhotoUrl) URL.revokeObjectURL(webUploadedPhotoUrl);
    setWebUploadedPhoto(null);
    setWebUploadedPhotoUrl('');
    setWebPhotoCredit('');
    setWebPhotoRightsAck(false);
    if (webPhotoInputRef.current) webPhotoInputRef.current.value = '';
  }, [webUploadedPhotoUrl]);

  /** Submit a web URL as a curated article. Uses the share endpoint which
   *  auto-approves with placement 'card', then refreshes the curate queue
   *  and switches to the Curate tab. */
  const handleWebSubmit = useCallback(async () => {
    if (!webUrl.trim() || webSubmitting) return;
    setWebSubmitting(true);
    setWebError(null);
    try {
      // Determine image override — curator-uploaded photo takes priority
      let imageUrlOverride: string | undefined;

      if (webUploadedPhoto && webPhotoRightsAck) {
        // Upload curator's key photo to MinIO, then use the returned URL
        const result = await uploadFile(webUploadedPhoto, 'news-photos', communityGuid);
        imageUrlOverride = fileUrl(result.key);
      } else if (webPreview) {
        // Fall back to OG image picker selection
        const allImages: string[] = [];
        if (webPreview.imageUrl) allImages.push(webPreview.imageUrl);
        if (webPreview.images) {
          for (const img of webPreview.images) {
            if (!allImages.includes(img)) allImages.push(img);
          }
        }
        const safeIdx = allImages.length > 0
          ? Math.min(webImageIndex, allImages.length - 1)
          : 0;
        const selected = allImages[safeIdx];
        // Only send override if the curator selected something other than the OG image
        if (selected && selected !== webPreview.imageUrl) {
          imageUrlOverride = selected;
        }
      }

      // Build comment — append photo credit if provided
      let comment = webComment.trim() || undefined;
      if (webPhotoCredit.trim() && webUploadedPhoto) {
        const creditLine = `Photo: ${webPhotoCredit.trim()}`;
        comment = comment ? `${comment}\n${creditLine}` : creditLine;
      }

      // DEV-966 / DEV-967: pass the curator's overrides only when they
      // differ from what the server scraped. Keeps the request payload
      // tight and lets the server's extraction chain stay authoritative
      // when the curator didn't customize.
      const scrapedDesc = (webPreview?.description ?? '').trim();
      const curatedDesc = webDescription.trim();
      const excerptOverride =
        curatedDesc && curatedDesc !== scrapedDesc ? curatedDesc : undefined;

      const scrapedTitle = (webPreview?.title ?? '').trim();
      const curatedTitle = webTitle.trim();
      const titleOverride =
        curatedTitle && curatedTitle !== scrapedTitle ? curatedTitle : undefined;

      await shareToCommunityFeed({
        scopeGuid: communityGuid,
        shareType: 'link',
        url: webUrl.trim(),
        comment,
        imageUrlOverride,
        excerptOverride,
        titleOverride,
      });
      setWebSuccess(true);
      // Stay on the Web tab — refresh the web items list and close the add modal
      setTimeout(async () => {
        try {
          // Refresh curate data in background (for consistency if user switches tabs)
          const [pending, approvedRaw] = await Promise.all([
            fetchPendingItems(communityGuid),
            fetchNewsFeed(communityGuid, { count: 50 }),
          ]);
          setPendingItems(pending);
          setApprovedItems(excludeFeedCompanions(approvedRaw));
        } catch {
          // Non-fatal
        }
        // Refresh web items list so the new link appears immediately
        loadWebItems();
        // Close add modal and reset form state
        setWebAddOpen(false);
        setWebUrl('');
        setWebComment('');
        setWebDescription(''); // DEV-966
        setWebTitle(''); // DEV-967
        setWebPreview(null);
        setWebImageIndex(0);
        setWebSuccess(false);
        // Reset key photo state
        if (webUploadedPhotoUrl) URL.revokeObjectURL(webUploadedPhotoUrl);
        setWebUploadedPhoto(null);
        setWebUploadedPhotoUrl('');
        setWebPhotoCredit('');
        setWebPhotoRightsAck(false);
      }, 1000);
    } catch (err: any) {
      const msg = err?.message || err?.body?.error || 'Failed to add article';
      setWebError(msg);
    } finally {
      setWebSubmitting(false);
    }
  }, [communityGuid, webUrl, webComment, webDescription, webTitle, webSubmitting, webPreview, webImageIndex, webUploadedPhoto, webUploadedPhotoUrl, webPhotoCredit, webPhotoRightsAck, loadWebItems]);

  // ── Close on Escape ─────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  // ── Configure: keyword management ──────────────────────
  const addKeyword = useCallback(() => {
    const kw = newKeyword.trim();
    if (!kw || keywords.includes(kw)) return;
    setKeywords((prev) => [...prev, kw]);
    setNewKeyword('');
    setConfigDirty(true);
  }, [newKeyword, keywords]);

  const removeKeyword = useCallback((kw: string) => {
    setKeywords((prev) => prev.filter((k) => k !== kw));
    setConfigDirty(true);
  }, []);

  // ── Configure: source toggling ─────────────────────────
  const toggleSource = useCallback((uri: string) => {
    setSelectedSources((prev) => {
      if (prev === null) {
        // Currently "all" → switch to specific: all except this one
        const allUris = masterSources.map((s) => s.sourceUri).filter((u) => u !== uri);
        setConfigDirty(true);
        return allUris;
      }
      if (prev.includes(uri)) {
        const next = prev.filter((u) => u !== uri);
        setConfigDirty(true);
        return next.length === 0 ? null : next;
      }
      setConfigDirty(true);
      return [...prev, uri];
    });
  }, [masterSources]);

  const isSourceSelected = useCallback((uri: string) => {
    if (selectedSources === null) return true; // all selected
    return selectedSources.includes(uri);
  }, [selectedSources]);

  // ── Configure: category exclusion toggling ──────────────
  const toggleExcludedCategory = useCallback((uri: string) => {
    setExcludedCategories((prev) => {
      const next = prev.includes(uri)
        ? prev.filter((u) => u !== uri)
        : [...prev, uri];
      setConfigDirty(true);
      return next;
    });
  }, []);

  // ── Configure: save subscription ───────────────────────
  const saveConfig = useCallback(async () => {
    setConfigSaving(true);
    try {
      const updated = await upsertSubscription(communityGuid, {
        keywords,
        allowedSourceUris: selectedSources,
        excludedCategories,
        moderationMode,
        maxArticles,
        isEnabled: true,
        scopeType: 'community',
      });
      setSubscription(updated);
      setConfigDirty(false);
    } catch (err: any) {
      const detail = err?.body?.detail || err?.body?.error || err?.message || '';
      console.error('[NewsCurate] Save config failed:', detail, err);
      setFetchResult(`Save failed: ${detail}`);
      throw err; // Re-throw so handleFetch aborts
    } finally {
      setConfigSaving(false);
    }
  }, [communityGuid, keywords, selectedSources, excludedCategories, moderationMode, maxArticles]);

  // ── Configure: fetch articles ──────────────────────────
  const handleFetch = useCallback(async () => {
    // Always save config before fetching — ensures subscription exists in DB
    if (!subscription || configDirty) await saveConfig();

    setFetchingNews(true);
    setFetchResult(null);
    try {
      const result = await fetchArticlesIntoQueue(communityGuid);
      setFetchResult(t('communityDetail.news.fetchSuccess').replace('{{count}}', String(result.fetched)));
      // Reload pending items
      const pending = await fetchPendingItems(communityGuid);
      setPendingItems(pending);
      // Auto-switch to Curate tab if articles were fetched
      if (result.fetched > 0) {
        setTab('curate');
      }
    } catch (err: any) {
      const detail = err?.body?.detail || err?.body?.error || err?.message || '';
      console.error('[NewsCurate] Fetch failed:', detail, err);
      setFetchResult(`${t('communityDetail.news.fetchError')}${detail ? ` (${detail})` : ''}`);
    } finally {
      setFetchingNews(false);
    }
  }, [communityGuid, configDirty, subscription, saveConfig, t]);

  // ── Curate: single item actions ────────────────────────
  const handleAction = useCallback(async (
    moderationGuid: string,
    action: 'approve' | 'reject',
    placement?: ArticlePlacement
  ) => {
    setActing(moderationGuid);
    try {
      // Default placement is 'card' unless explicitly set (e.g. 'featured')
      const effectivePlacement = placement || 'card';
      const actions: ModerationAction[] = [{
        moderationGuid,
        action,
        placement: action === 'approve' ? effectivePlacement : undefined,
      }];
      await moderateItems(actions);
      // Move from pending to approved or remove
      if (action === 'approve') {
        const item = pendingItems.find((i) => i.moderationGuid === moderationGuid);
        if (item) {
          const approved = { ...item, status: 'approved' as const, placement: effectivePlacement };
          setApprovedItems((prev) => [approved, ...prev]);
        }
      }
      setPendingItems((prev) => prev.filter((i) => i.moderationGuid !== moderationGuid));
    } catch {
      // Silently fail
    } finally {
      setActing(null);
    }
  }, [pendingItems]);

  // ── Curate: bulk approve ───────────────────────────────
  const handleBulkApprove = useCallback(async () => {
    setActing('bulk');
    try {
      const actions: ModerationAction[] = pendingItems.map((i) => ({
        moderationGuid: i.moderationGuid,
        action: 'approve' as const,
        placement: 'card' as ArticlePlacement,
      }));
      await moderateItems(actions);
      setApprovedItems((prev) => [
        ...pendingItems.map((i) => ({ ...i, status: 'approved' as const, placement: 'card' as ArticlePlacement })),
        ...prev,
      ]);
      setPendingItems([]);
    } catch {
      // Fail
    } finally {
      setActing(null);
    }
  }, [pendingItems]);

  // ── Curate: change placement on approved item ──────────
  const handlePlacementChange = useCallback(async (
    moderationGuid: string,
    placement: ArticlePlacement
  ) => {
    setActing(moderationGuid);
    try {
      await updateArticlePlacement(moderationGuid, placement);
      setApprovedItems((prev) => prev.map((i) => {
        if (i.moderationGuid === moderationGuid) {
          return { ...i, placement, isFeatured: placement === 'featured' };
        }
        // Demote any other featured item to 'card' when a new one is promoted
        if (placement === 'featured' && (i.placement === 'featured' || i.isFeatured)) {
          return { ...i, placement: 'card' as ArticlePlacement, isFeatured: false };
        }
        return i;
      }));
    } catch {
      // Fail
    } finally {
      setActing(null);
    }
  }, []);

  // ── Curate: reject approved item ───────────────────────
  //    Refreshes from server afterwards so that backend auto-promotion
  //    (e.g. next card promoted to featured) is reflected in the UI.
  const handleRemoveApproved = useCallback(async (moderationGuid: string) => {
    setActing(moderationGuid);
    try {
      await moderateItems([{ moderationGuid, action: 'reject' }]);
      // Refresh from server to pick up any auto-promotion changes
      try {
        const approvedRaw = await fetchNewsFeed(communityGuid, { count: 50 });
        setApprovedItems(excludeFeedCompanions(approvedRaw));
      } catch {
        // Fall back to local removal if refresh fails
        setApprovedItems((prev) => prev.filter((i) => i.moderationGuid !== moderationGuid));
      }
    } catch {
      // Fail
    } finally {
      setActing(null);
    }
  }, [communityGuid]);

  // ── Filtered sources for the list ──────────────────────
  const filteredSources = useMemo(() => {
    if (!sourceFilter.trim()) return masterSources;
    const q = sourceFilter.toLowerCase();
    return masterSources.filter(
      (s) => s.sourceName.toLowerCase().includes(q) || (s.sourceDomain || '').toLowerCase().includes(q)
    );
  }, [masterSources, sourceFilter]);

  // ── Curate: filtered item lists ──────────────────────────
  const curateQuery = curateSearch.toLowerCase().trim();
  const filterCurateItem = useCallback((item: NewsModerationItem) => {
    if (!curateQuery) return true;
    const a = item.article;
    return (
      (a?.title || '').toLowerCase().includes(curateQuery) ||
      (a?.sourceName || '').toLowerCase().includes(curateQuery) ||
      (a?.sourceDomain || '').toLowerCase().includes(curateQuery) ||
      (a?.category || '').toLowerCase().includes(curateQuery) ||
      (a?.excerpt || '').toLowerCase().includes(curateQuery)
    );
  }, [curateQuery]);

  const filteredPending = useMemo(() => pendingItems.filter(filterCurateItem), [pendingItems, filterCurateItem]);
  const filteredApproved = useMemo(() => approvedItems.filter(filterCurateItem), [approvedItems, filterCurateItem]);

  // ── Render ──────────────────────────────────────────────
  if (!isOpen) return null;

  return (
    <div className={styles.overlay} onClick={(e) => {
      if (e.target === e.currentTarget) onClose();
    }}>
      <div className={styles.modal}>
        {/* Header with tabs */}
        <div className={styles.header}>
          <div className={styles.headerTabs}>
            <button
              className={`${styles.headerTab} ${tab === 'sources' ? styles.headerTabActive : ''}`}
              onClick={() => {
                setTab('sources');
                // If current subtab is newsapi but API isn't enabled, switch to pool
                if (!apiEnabled && sourcesSubTab === 'newsapi') setSourcesSubTab('pool');
              }}
            >
              {t('communityDetail.news.sourcesTab')}
            </button>
            <button
              className={`${styles.headerTab} ${tab === 'curate' ? styles.headerTabActive : ''}`}
              onClick={() => setTab('curate')}
            >
              {t('communityDetail.news.curateTab')}
              {pendingItems.length > 0 && (
                <span className={styles.pendingBadge}>{pendingItems.length}</span>
              )}
            </button>
            <button
              className={`${styles.headerTab} ${tab === 'preview' ? styles.headerTabActive : ''}`}
              onClick={() => setTab('preview')}
            >
              {t('communityDetail.news.previewTab')}
            </button>
          </div>
          <CloseButton onClick={onClose} size="md" />
        </div>

        {/* Sources subtabs */}
        {tab === 'sources' && (
          <div className={styles.subTabBar}>
            {apiEnabled && (
              <button
                className={`${styles.subTab} ${sourcesSubTab === 'newsapi' ? styles.subTabActive : ''}`}
                onClick={() => setSourcesSubTab('newsapi')}
              >
                {t('communityDetail.news.newsApiTab')}
              </button>
            )}
            <button
              className={`${styles.subTab} ${sourcesSubTab === 'pool' ? styles.subTabActive : ''}`}
              onClick={() => setSourcesSubTab('pool')}
            >
              {t('communityDetail.news.poolTab')}
            </button>
            <button
              className={`${styles.subTab} ${sourcesSubTab === 'l4e' ? styles.subTabActive : ''}`}
              onClick={() => setSourcesSubTab('l4e')}
            >
              {t('communityDetail.news.l4eTab')}
            </button>
            <button
              className={`${styles.subTab} ${sourcesSubTab === 'web' ? styles.subTabActive : ''}`}
              onClick={() => setSourcesSubTab('web')}
            >
              {t('communityDetail.news.webTab')}
            </button>
          </div>
        )}

        {/* ────────────────────────────────────────────────── */}
        {/* SOURCES > NEWS API SUBTAB                          */}
        {/* ────────────────────────────────────────────────── */}
        {tab === 'sources' && sourcesSubTab === 'newsapi' && (
          <div className={styles.body}>
            {configLoading ? (
              <div className={styles.emptyState}>{t('communityDetail.news.loading')}</div>
            ) : (
              <div className={styles.configContent}>
                {/* Keywords */}
                <section className={styles.section}>
                  <h3 className={styles.sectionTitle}>
                    {t('communityDetail.news.keywordsTitle')}
                  </h3>
                  <p className={styles.sectionHint}>
                    {t('communityDetail.news.keywordsHint')}
                  </p>
                  <div className={styles.keywordRow}>
                    <input
                      type="text"
                      className={styles.keywordInput}
                      value={newKeyword}
                      onChange={(e) => setNewKeyword(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') addKeyword(); }}
                      placeholder={t('communityDetail.news.keywordPlaceholder')}
                    />
                    <button
                      className={styles.addBtn}
                      onClick={addKeyword}
                      disabled={!newKeyword.trim()}
                    >
                      +
                    </button>
                  </div>
                  <div className={styles.keywordChips}>
                    {keywords.map((kw) => (
                      <span key={kw} className={styles.keywordChip}>
                        {kw}
                        <button
                          className={styles.chipRemove}
                          onClick={() => removeKeyword(kw)}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                    {keywords.length === 0 && (
                      <span className={styles.noKeywords}>
                        {t('communityDetail.news.noKeywords')}
                      </span>
                    )}
                  </div>
                </section>

                {/* Sources */}
                <section className={styles.section}>
                  <h3 className={styles.sectionTitle}>
                    {t('communityDetail.news.sourcesTitle')}
                  </h3>
                  <p className={styles.sectionHint}>
                    {t('communityDetail.news.sourcesHint')}
                  </p>
                  <input
                    type="text"
                    className={styles.sourceSearch}
                    value={sourceFilter}
                    onChange={(e) => setSourceFilter(e.target.value)}
                    placeholder={t('communityDetail.news.sourceSearchPlaceholder')}
                  />
                  <div className={styles.sourceList}>
                    {filteredSources.length === 0 ? (
                      <div className={styles.noSources}>
                        {t('communityDetail.news.noSources')}
                      </div>
                    ) : (
                      filteredSources.map((src) => (
                        <label key={src.sourceUri} className={styles.sourceItem}>
                          <input
                            type="checkbox"
                            checked={isSourceSelected(src.sourceUri)}
                            onChange={() => toggleSource(src.sourceUri)}
                          />
                          <span className={styles.sourceName}>{src.sourceName}</span>
                          {src.sourceDomain && (
                            <span className={styles.sourceDomain}>{src.sourceDomain}</span>
                          )}
                        </label>
                      ))
                    )}
                  </div>
                </section>

                {/* Excluded Categories */}
                <section className={styles.section}>
                  <h3 className={styles.sectionTitle}>
                    {t('communityDetail.news.excludedCategoriesTitle')}
                  </h3>
                  <p className={styles.sectionHint}>
                    {t('communityDetail.news.excludedCategoriesHint')}
                  </p>
                  <div className={styles.categoryCheckboxes}>
                    {CATEGORY_OPTIONS.map((cat) => (
                      <label key={cat.uri} className={styles.categoryItem}>
                        <input
                          type="checkbox"
                          checked={excludedCategories.includes(cat.uri)}
                          onChange={() => toggleExcludedCategory(cat.uri)}
                        />
                        <span>{t(`communityDetail.news.${cat.labelKey}`)}</span>
                      </label>
                    ))}
                  </div>
                </section>

                {/* Settings row */}
                <section className={styles.section}>
                  <h3 className={styles.sectionTitle}>
                    {t('communityDetail.news.settingsTitle')}
                  </h3>
                  <div className={styles.settingsGrid}>
                    <label className={styles.settingsLabel}>
                      {t('communityDetail.news.moderationLabel')}
                      <select
                        className={styles.settingsSelect}
                        value={moderationMode}
                        onChange={(e) => {
                          setModerationMode(e.target.value as 'queue' | 'auto_approve');
                          setConfigDirty(true);
                        }}
                      >
                        <option value="queue">{t('communityDetail.news.moderationQueue')}</option>
                        <option value="auto_approve">{t('communityDetail.news.moderationAuto')}</option>
                      </select>
                    </label>
                    <label className={styles.settingsLabel}>
                      {t('communityDetail.news.maxArticlesLabel')}
                      <input
                        type="number"
                        className={styles.settingsInput}
                        value={maxArticles}
                        min={1}
                        max={50}
                        onChange={(e) => {
                          setMaxArticles(Number(e.target.value) || 15);
                          setConfigDirty(true);
                        }}
                      />
                    </label>
                  </div>
                </section>
              </div>
            )}

            {/* Footer: Save + Fetch */}
            {!configLoading && (
              <div className={styles.configFooter}>
                {fetchResult && (
                  <span className={styles.fetchResultMsg}>{fetchResult}</span>
                )}
                <div className={styles.configActions}>
                  {configDirty && (
                    <button
                      className={styles.saveBtn}
                      onClick={saveConfig}
                      disabled={configSaving}
                    >
                      {configSaving
                        ? t('communityDetail.news.saving')
                        : t('communityDetail.news.saveConfig')}
                    </button>
                  )}
                  <button
                    className={styles.fetchBtn}
                    onClick={handleFetch}
                    disabled={fetchingNews || keywords.length === 0}
                  >
                    {fetchingNews
                      ? t('communityDetail.news.fetching')
                      : t('communityDetail.news.fetchArticles')}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ────────────────────────────────────────────────── */}
        {/* CURATE TAB                                         */}
        {/* ────────────────────────────────────────────────── */}
        {tab === 'curate' && (
          <div className={styles.body}>
            {curateLoading ? (
              <div className={styles.emptyState}>{t('communityDetail.news.loading')}</div>
            ) : (
              <div className={styles.curateContent}>
                {/* Search bar */}
                <div className={styles.curateSearchRow}>
                  <input
                    type="text"
                    className={styles.keywordInput}
                    value={curateSearch}
                    onChange={(e) => setCurateSearch(e.target.value)}
                    placeholder={t('communityDetail.news.curateSearchPlaceholder')}
                  />
                  {curateSearch && (
                    <button
                      className={styles.curateSearchClear}
                      onClick={() => setCurateSearch('')}
                      title={t('communityDetail.news.clearSearch')}
                    >
                      ✕
                    </button>
                  )}
                </div>

                {/* Pending section */}
                <section className={styles.section}>
                  <div className={styles.sectionHeader}>
                    <h3 className={styles.sectionTitle}>
                      {t('communityDetail.news.pendingTitle')}
                      <span className={styles.sectionCount}>{filteredPending.length}</span>
                    </h3>
                    {filteredPending.length > 1 && (
                      <button
                        className={styles.bulkApproveBtn}
                        disabled={acting === 'bulk'}
                        onClick={handleBulkApprove}
                      >
                        {t('communityDetail.news.approveAll')}
                      </button>
                    )}
                  </div>

                  {filteredPending.length === 0 ? (
                    <div className={styles.queueEmpty}>
                      {curateSearch
                        ? t('communityDetail.news.curateNoResults')
                        : t('communityDetail.news.queueEmpty')}
                    </div>
                  ) : (
                    <div className={styles.itemList}>
                      {filteredPending.map((item) => {
                        const article = item.article;
                        const badge = contentTypeBadge(item.contentType);
                        const isActing = acting === item.moderationGuid || acting === 'bulk';

                        return (
                          <div key={item.moderationGuid} className={styles.curateItem}>
                            {article?.imageUrl ? (
                              <img className={styles.itemThumb} src={mediaSrc(article.imageUrl) ?? undefined} alt="" loading="lazy" />
                            ) : (
                              <div className={styles.itemThumbPlaceholder}>📄</div>
                            )}
                            <div className={styles.itemContent}>
                              <div className={styles.itemSource}>
                                {article?.sourceName || article?.sourceDomain || '—'}
                              </div>
                              <h4 className={styles.itemTitle}>{article?.title || '—'}</h4>
                              <div className={styles.itemMeta}>
                                <span className={`${styles.typeBadge} ${badge.cls}`}>{badge.label}</span>
                                <span>{formatDate(article?.publishedAt)}</span>
                              </div>
                            </div>
                            <div className={styles.itemActions}>
                              {article?.canonicalUrl && (
                                <a
                                  className={styles.openArticleBtn}
                                  href={article.canonicalUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  title={t('communityDetail.news.openArticle')}
                                >
                                  ↗
                                </a>
                              )}
                              <button
                                className={styles.approveBtn}
                                disabled={isActing}
                                onClick={() => handleAction(item.moderationGuid, 'approve')}
                                title={t('communityDetail.news.approve')}
                              >
                                ✓
                              </button>
                              <button
                                className={styles.featureBtn}
                                disabled={isActing}
                                onClick={() => handleAction(item.moderationGuid, 'approve', 'featured')}
                                title={t('communityDetail.news.feature')}
                              >
                                ★
                              </button>
                              <button
                                className={styles.rejectBtn}
                                disabled={isActing}
                                onClick={() => handleAction(item.moderationGuid, 'reject')}
                                title={t('communityDetail.news.reject')}
                              >
                                ✕
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </section>

                {/* Approved / active articles — grouped by placement, sorted by date */}
                <section className={styles.section}>
                  <h3 className={styles.sectionTitle}>
                    {t('communityDetail.news.approvedTitle')}
                    <span className={styles.sectionCount}>{filteredApproved.length}</span>
                  </h3>

                  {filteredApproved.length === 0 ? (
                    <div className={styles.queueEmpty}>
                      {curateSearch
                        ? t('communityDetail.news.curateNoResults')
                        : t('communityDetail.news.noApproved')}
                    </div>
                  ) : (
                    <div className={styles.itemList}>
                      {[...filteredApproved]
                        .sort((a, b) => {
                          // Group order: featured=0, card=1, headline=2, unset=3
                          const placementOrder = (p?: string, feat?: boolean) => {
                            if (p === 'featured' || (!p && feat)) return 0;
                            if (p === 'card') return 1;
                            if (p === 'headline') return 2;
                            return 3;
                          };
                          const pa = placementOrder(a.placement, a.isFeatured);
                          const pb = placementOrder(b.placement, b.isFeatured);
                          if (pa !== pb) return pa - pb;
                          // Within same group, sort by publish date descending
                          const da = a.article?.publishedAt ? new Date(a.article.publishedAt).getTime() : 0;
                          const db = b.article?.publishedAt ? new Date(b.article.publishedAt).getTime() : 0;
                          return db - da;
                        })
                        .map((item) => {
                        const article = item.article;
                        const isActing = acting === item.moderationGuid;

                        return (
                          <div key={item.moderationGuid} className={styles.curateItem}>
                            {article?.imageUrl ? (
                              <img className={styles.itemThumb} src={mediaSrc(article.imageUrl) ?? undefined} alt="" loading="lazy" />
                            ) : (
                              <div className={styles.itemThumbPlaceholder}>📄</div>
                            )}
                            <div className={styles.itemContent}>
                              <div className={styles.itemSource}>
                                {article?.sourceName || article?.sourceDomain || '—'}
                              </div>
                              <h4 className={styles.itemTitle}>{article?.title || '—'}</h4>
                              <div className={styles.itemMeta}>
                                <span>{formatDate(article?.publishedAt)}</span>
                              </div>
                            </div>
                            <div className={styles.placementControls}>
                              {article?.canonicalUrl && (
                                <a
                                  className={styles.openArticleBtn}
                                  href={article.canonicalUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  title={t('communityDetail.news.openArticle')}
                                >
                                  ↗
                                </a>
                              )}
                              <select
                                className={styles.placementSelect}
                                value={item.placement || (item.isFeatured ? 'featured' : 'headline')}
                                disabled={isActing}
                                onChange={(e) =>
                                  handlePlacementChange(
                                    item.moderationGuid,
                                    e.target.value as ArticlePlacement
                                  )
                                }
                              >
                                <option value="featured">{t('communityDetail.news.placementFeatured')}</option>
                                <option value="card">{t('communityDetail.news.placementCard')}</option>
                                <option value="headline">{t('communityDetail.news.placementHeadline')}</option>
                              </select>
                              <button
                                className={styles.removeBtn}
                                disabled={isActing}
                                onClick={() => handleRemoveApproved(item.moderationGuid)}
                                title={t('communityDetail.news.remove')}
                              >
                                ✕
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </section>
              </div>
            )}
          </div>
        )}

        {/* ────────────────────────────────────────────────── */}
        {/* SOURCES > ARTICLE POOL SUBTAB                      */}
        {/* ────────────────────────────────────────────────── */}
        {tab === 'sources' && sourcesSubTab === 'pool' && (
          <div className={styles.body}>
            <div className={styles.poolContent}>
              <p className={styles.sectionHint}>
                {t('communityDetail.news.poolHint')}
              </p>

              {/* Search bar */}
              <div className={styles.poolSearchRow}>
                <input
                  type="text"
                  className={styles.keywordInput}
                  value={poolSearch}
                  onChange={(e) => setPoolSearch(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') loadPool(1, poolSearch); }}
                  placeholder={t('communityDetail.news.poolSearchPlaceholder')}
                />
                <button
                  className={styles.saveBtn}
                  onClick={() => loadPool(1, poolSearch)}
                >
                  {t('communityDetail.news.poolSearchBtn')}
                </button>
              </div>

              {/* Results — grouped by time bucket */}
              {poolLoading ? (
                <div className={styles.emptyState}>{t('communityDetail.news.loading')}</div>
              ) : poolArticles.length === 0 ? (
                <div className={styles.queueEmpty}>
                  {t('communityDetail.news.poolEmpty')}
                </div>
              ) : (
                <div className={styles.poolBucketList}>
                  {TIME_BUCKET_ORDER.map((bucket) => {
                    const items = poolBuckets.get(bucket) ?? [];
                    if (items.length === 0) return null;
                    const isCollapsed = poolCollapsed.has(bucket);
                    return (
                      <div key={bucket} className={styles.poolBucket}>
                        <button
                          type="button"
                          className={styles.poolBucketHeader}
                          onClick={() => isCollapsed ? expandPoolBucket(bucket) : collapsePoolBucket(bucket)}
                          aria-expanded={!isCollapsed}
                        >
                          <span className={`${styles.poolBucketChevron} ${isCollapsed ? styles.poolBucketChevronCollapsed : ''}`}>
                            ▾
                          </span>
                          <span className={styles.poolBucketLabel}>
                            {t(`communityDetail.news.bucket_${bucket}`)}
                          </span>
                          <span className={styles.poolBucketCount}>{items.length}</span>
                        </button>
                        {!isCollapsed && (
                          <div className={styles.itemList}>
                            {items.map((article) => (
                              <div
                                key={article.newsItemGuid}
                                className={`${styles.curateItem} ${poolSelected.has(article.newsItemGuid) ? styles.poolItemSelected : ''}`}
                              >
                                <input
                                  type="checkbox"
                                  className={styles.poolCheckbox}
                                  checked={poolSelected.has(article.newsItemGuid)}
                                  onChange={() => togglePoolSelection(article.newsItemGuid)}
                                />
                                {article.imageUrl ? (
                                  <img className={styles.itemThumb} src={mediaSrc(article.imageUrl) ?? undefined} alt="" loading="lazy" />
                                ) : (
                                  <div className={styles.itemThumbPlaceholder}>📄</div>
                                )}
                                <div className={styles.itemContent}>
                                  <div className={styles.itemSource}>
                                    {article.sourceName || article.sourceDomain || '—'}
                                  </div>
                                  <h4 className={styles.itemTitle}>{article.title}</h4>
                                  <div className={styles.itemMeta}>
                                    <span>{formatDate(article.publishedAt)}</span>
                                    {article.category && <span>{article.category}</span>}
                                    {article.approvedByCount > 0 && (
                                      <span className={styles.poolUsedBadge}>
                                        {t('communityDetail.news.poolUsedBy').replace('{{count}}', String(article.approvedByCount))}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Pagination */}
              {poolTotal > 20 && (
                <div className={styles.poolPagination}>
                  <button
                    className={styles.saveBtn}
                    disabled={poolPage <= 1}
                    onClick={() => loadPool(poolPage - 1, poolSearch)}
                  >
                    ←
                  </button>
                  <span className={styles.poolPageInfo}>
                    {t('communityDetail.news.poolPage')
                      .replace('{{page}}', String(poolPage))
                      .replace('{{total}}', String(Math.ceil(poolTotal / 20)))}
                  </span>
                  <button
                    className={styles.saveBtn}
                    disabled={poolPage * 20 >= poolTotal}
                    onClick={() => loadPool(poolPage + 1, poolSearch)}
                  >
                    →
                  </button>
                </div>
              )}
            </div>

            {/* Adopt footer — sticky at bottom */}
            {poolSelected.size > 0 && (
              <div className={styles.configFooter}>
                <span className={styles.fetchResultMsg}>
                  {t('communityDetail.news.poolSelectedCount').replace('{{count}}', String(poolSelected.size))}
                </span>
                <div className={styles.configActions}>
                  <button
                    className={styles.fetchBtn}
                    onClick={handleAdopt}
                    disabled={poolAdopting}
                  >
                    {poolAdopting
                      ? t('communityDetail.news.poolAdopting')
                      : t('communityDetail.news.poolAdoptBtn')}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ────────────────────────────────────────────────── */}
        {/* SOURCES > LIFE4EARTH SUBTAB                        */}
        {/* ────────────────────────────────────────────────── */}
        {tab === 'sources' && sourcesSubTab === 'l4e' && (
          <div className={styles.body}>
            <div className={styles.l4eContent}>
              <p className={styles.sectionHint}>
                {t('communityDetail.news.l4eHint')}
              </p>

              {l4eLoading ? (
                <div className={styles.emptyState}>{t('communityDetail.news.loading')}</div>
              ) : l4eItems.length === 0 ? (
                <div className={styles.l4ePlaceholder}>
                  <span className={styles.l4eIcon}>🌍</span>
                  <h3 className={styles.l4eTitle}>{t('communityDetail.news.l4eTitle')}</h3>
                  <p className={styles.l4eEmptyMsg}>{t('communityDetail.news.l4eEmpty')}</p>
                </div>
              ) : (
                <div className={styles.itemList}>
                  {l4eItems.map((item) => {
                    const a = item.article;
                    if (!a) return null;
                    const badge = contentTypeBadge(a.contentType);
                    return (
                      <div
                        key={item.moderationGuid}
                        className={`${styles.curateItem} ${l4eSelected.has(item.newsItemGuid) ? styles.poolItemSelected : ''}`}
                      >
                        <input
                          type="checkbox"
                          className={styles.poolCheckbox}
                          checked={l4eSelected.has(item.newsItemGuid)}
                          onChange={() => toggleL4eSelection(item.newsItemGuid)}
                        />
                        {a.imageUrl ? (
                          <img className={styles.itemThumb} src={mediaSrc(a.imageUrl) ?? undefined} alt="" loading="lazy" />
                        ) : (
                          <div className={styles.itemThumbPlaceholder}>
                            {a.chronicleGuid ? '📋' : a.embedType === 'video' ? '▶️' : '🔗'}
                          </div>
                        )}
                        <div className={styles.itemContent}>
                          <div className={styles.itemSource}>
                            <span className={badge.cls}>{badge.label}</span>
                            {a.sourceName || a.sourceDomain || 'Shared'}
                          </div>
                          <h4 className={styles.itemTitle}>{a.title}</h4>
                          {a.shareComment && (
                            <div className={styles.itemExcerpt}>"{a.shareComment}"</div>
                          )}
                          <div className={styles.itemMeta}>
                            <span>{formatDate(a.publishedAt)}</span>
                            {a.category && <span>{a.category}</span>}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer — jump to curate tab to manage placement */}
            {l4eSelected.size > 0 && (
              <div className={styles.configFooter}>
                <span className={styles.fetchResultMsg}>
                  {t('communityDetail.news.l4eSelectedCount').replace('{{count}}', String(l4eSelected.size))}
                </span>
                <div className={styles.configActions}>
                  <button
                    className={styles.fetchBtn}
                    onClick={handleViewInCurate}
                  >
                    {t('communityDetail.news.l4eViewInCurate')}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ────────────────────────────────────────────────── */}
        {/* SOURCES > WEB SUBTAB (list + add-link modal)       */}
        {/* ────────────────────────────────────────────────── */}
        {tab === 'sources' && sourcesSubTab === 'web' && (
          <div className={styles.body}>
            <div className={styles.l4eContent}>
              {/* Header row with hint + Add button */}
              <div className={styles.webHeaderRow}>
                <p className={styles.sectionHint} style={{ margin: 0 }}>
                  {t('communityDetail.news.webHint')}
                </p>
                <button
                  className={styles.webAddBtn}
                  onClick={() => setWebAddOpen(true)}
                >
                  + {t('communityDetail.news.webAddBtn')}
                </button>
              </div>

              {/* ── Inline add-link modal (overlay card) ─── */}
              {webAddOpen && (
                <div className={styles.webAddOverlay}>
                  <div className={styles.webAddModal}>
                    <div className={styles.webAddModalHeader}>
                      <h4 className={styles.webAddModalTitle}>
                        {t('communityDetail.news.webAddTitle')}
                      </h4>
                      <button
                        className={styles.webAddModalClose}
                        onClick={() => {
                          setWebAddOpen(false);
                          setWebUrl('');
                          setWebComment('');
                          setWebPreview(null);
                          setWebPreviewError(false);
                          setWebError(null);
                          setWebImageIndex(0);
                        }}
                        aria-label="Close"
                      >
                        ×
                      </button>
                    </div>

                    {/* URL input */}
                    <div className={styles.webField}>
                      <input
                        type="text"
                        value={webUrl}
                        onChange={(e) => setWebUrl(e.target.value)}
                        placeholder={t('communityDetail.news.webUrlPlaceholder')}
                        className={styles.webInput}
                        autoFocus
                      />
                    </div>

                    {/* Preview loading */}
                    {webPreviewLoading && (
                      <div className={styles.webPreviewLoading}>
                        {t('communityDetail.news.webPreviewLoading')}
                      </div>
                    )}

                    {/* Preview error */}
                    {webPreviewError && !webPreviewLoading && webUrl.trim().length > 10 && (
                      <div className={styles.webPreviewError}>
                        {t('communityDetail.news.webPreviewError')}
                      </div>
                    )}

                    {/* Hidden file input for key photo upload */}
                    <input
                      ref={webPhotoInputRef}
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={handleWebPhotoSelect}
                    />

                    {/* OG Preview card with image picker + key photo upload */}
                    {webPreview && !webPreviewLoading && (() => {
                      const allImages: string[] = [];
                      if (webPreview.imageUrl) allImages.push(webPreview.imageUrl);
                      if (webPreview.images) {
                        for (const img of webPreview.images) {
                          if (!allImages.includes(img)) allImages.push(img);
                        }
                      }
                      const safeIndex = allImages.length > 0
                        ? Math.min(webImageIndex, allImages.length - 1)
                        : 0;
                      const currentImage = allImages[safeIndex] ?? null;
                      const hasMultiple = allImages.length > 1;
                      const hasUploadedPhoto = !!webUploadedPhotoUrl;

                      return (
                        <div className={styles.webPreviewCard}>
                          {webPreview.embedHtml && webPreview.embedType === 'video' ? (
                            <div
                              className={styles.webEmbedContainer}
                              dangerouslySetInnerHTML={{ __html: sanitizeEmbed(webPreview.embedHtml) }}
                            />
                          ) : hasUploadedPhoto ? (
                            /* Curator-uploaded key photo — takes priority */
                            <div className={styles.webImagePickerWrap}>
                              <img src={webUploadedPhotoUrl} alt="" className={styles.webPreviewImage} />
                              <button
                                type="button"
                                className={styles.webPhotoRemoveBtn}
                                onClick={handleWebPhotoRemove}
                                aria-label={t('communityDetail.news.webPhotoRemove')}
                              >
                                ✕
                              </button>
                              <div className={styles.webImageCounter}>
                                {t('communityDetail.news.webPhotoUploaded')}
                              </div>
                            </div>
                          ) : currentImage ? (
                            <div className={styles.webImagePickerWrap}>
                              <img src={currentImage} alt="" className={styles.webPreviewImage} />
                              {hasMultiple && (
                                <>
                                  <button
                                    type="button"
                                    className={`${styles.webImageArrow} ${styles.webImageArrowLeft}`}
                                    onClick={() => setWebImageIndex((safeIndex - 1 + allImages.length) % allImages.length)}
                                    aria-label={t('communityDetail.news.webImagePrev')}
                                  >
                                    ‹
                                  </button>
                                  <button
                                    type="button"
                                    className={`${styles.webImageArrow} ${styles.webImageArrowRight}`}
                                    onClick={() => setWebImageIndex((safeIndex + 1) % allImages.length)}
                                    aria-label={t('communityDetail.news.webImageNext')}
                                  >
                                    ›
                                  </button>
                                  <div className={styles.webImageCounter}>
                                    {safeIndex + 1} / {allImages.length}
                                  </div>
                                </>
                              )}
                              {/* "Upload custom photo" link below the OG image picker */}
                              <button
                                type="button"
                                className={styles.webPhotoUploadOverlay}
                                onClick={() => webPhotoInputRef.current?.click()}
                              >
                                {t('communityDetail.news.webPhotoUploadAlt')}
                              </button>
                            </div>
                          ) : (
                            /* No images found — primary upload prompt */
                            <div className={styles.webNoImage}>
                              <div>{t('communityDetail.news.webNoImage')}</div>
                              <button
                                type="button"
                                className={styles.webPhotoUploadBtn}
                                onClick={() => webPhotoInputRef.current?.click()}
                              >
                                {t('communityDetail.news.webPhotoUpload')}
                              </button>
                              <div className={styles.webPhotoHint}>
                                {t('communityDetail.news.webPhotoHint')}
                              </div>
                            </div>
                          )}

                          {/* Rights acknowledgment + credit (shown when a photo is uploaded) */}
                          {hasUploadedPhoto && (
                            <div className={styles.webPhotoMeta}>
                              <label className={styles.webPhotoRightsLabel}>
                                <input
                                  type="checkbox"
                                  checked={webPhotoRightsAck}
                                  onChange={(e) => setWebPhotoRightsAck(e.target.checked)}
                                />
                                <span>{t('communityDetail.news.webPhotoRightsAck')}</span>
                              </label>
                              <input
                                type="text"
                                value={webPhotoCredit}
                                onChange={(e) => setWebPhotoCredit(e.target.value)}
                                placeholder={t('communityDetail.news.webPhotoCreditPlaceholder')}
                                className={styles.webPhotoCreditInput}
                              />
                            </div>
                          )}

                          <div className={styles.webPreviewBody}>
                            <div className={styles.webPreviewTitle}>
                              {webPreview.title || webUrl}
                            </div>
                            {webPreview.description && (
                              <div className={styles.webPreviewDesc}>
                                {webPreview.description.slice(0, 200)}
                              </div>
                            )}
                            {webPreview.siteName && (
                              <div className={styles.webPreviewSource}>
                                {webPreview.siteName}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })()}

                    {/* DEV-967: Curator-editable title — auto-fills from
                        the server's scraped title (og / twitter / meta /
                        JSON-LD headline / article h1 / <title> / URL-
                        prettify). Curator can edit or paste their own. */}
                    {webPreview && !webPreviewLoading && (
                      <div className={styles.webField}>
                        <label
                          style={{
                            display: 'block',
                            fontSize: 13,
                            fontWeight: 500,
                            color: 'var(--text-primary)',
                            marginBlockEnd: 4,
                          }}
                        >
                          {t('communityDetail.news.webTitleLabel')}
                        </label>
                        <input
                          type="text"
                          value={webTitle}
                          onChange={(e) => setWebTitle(e.target.value)}
                          placeholder={t('communityDetail.news.webTitlePlaceholder')}
                          className={styles.webTextarea}
                          style={{ inlineSize: '100%' }}
                        />
                        <div
                          style={{
                            fontSize: 11,
                            color: 'var(--text-muted)',
                            marginBlockStart: 4,
                          }}
                        >
                          {t('communityDetail.news.webTitleHint')}
                        </div>
                      </div>
                    )}

                    {/* DEV-966: Curator-editable intro text — auto-fills
                        from the server's scraped description (og / twitter /
                        meta / JSON-LD / first paragraph). Curator can edit
                        or paste their own when the page lacks metadata. */}
                    {webPreview && !webPreviewLoading && (
                      <div className={styles.webField}>
                        <label
                          style={{
                            display: 'block',
                            fontSize: 13,
                            fontWeight: 500,
                            color: 'var(--text-primary)',
                            marginBlockEnd: 4,
                          }}
                        >
                          {t('communityDetail.news.webDescriptionLabel')}
                        </label>
                        <textarea
                          value={webDescription}
                          onChange={(e) => setWebDescription(e.target.value)}
                          placeholder={t('communityDetail.news.webDescriptionPlaceholder')}
                          className={styles.webTextarea}
                          rows={3}
                        />
                        <div
                          style={{
                            fontSize: 11,
                            color: 'var(--text-muted)',
                            marginBlockStart: 4,
                          }}
                        >
                          {t('communityDetail.news.webDescriptionHint')}
                        </div>
                      </div>
                    )}

                    {/* Optional comment */}
                    {webPreview && !webPreviewLoading && (
                      <div className={styles.webField}>
                        <textarea
                          value={webComment}
                          onChange={(e) => setWebComment(e.target.value)}
                          placeholder={t('communityDetail.news.webCommentPlaceholder')}
                          className={styles.webTextarea}
                          rows={2}
                        />
                      </div>
                    )}

                    {/* Add-modal footer */}
                    <div className={styles.webAddModalFooter}>
                      {webError && (
                        <span className={styles.fetchResultMsg} style={{ color: 'var(--status-error)' }}>
                          {webError}
                        </span>
                      )}
                      {webSuccess && (
                        <span className={styles.fetchResultMsg} style={{ color: 'var(--status-success)' }}>
                          {t('communityDetail.news.webSuccess')}
                        </span>
                      )}
                      <button
                        className={styles.fetchBtn}
                        onClick={handleWebSubmit}
                        disabled={webSubmitting || webSuccess || !webUrl.trim() || webUrl.trim().length < 10 || (!!webUploadedPhoto && !webPhotoRightsAck)}
                      >
                        {webSubmitting
                          ? t('communityDetail.news.webSubmitting')
                          : t('communityDetail.news.webSubmit')}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* ── Web items list ──────────────────────── */}
              {webItemsLoading ? (
                <div className={styles.emptyState}>{t('communityDetail.news.loading')}</div>
              ) : webItems.length === 0 ? (
                <div className={styles.l4ePlaceholder}>
                  <span className={styles.l4eIcon}>🌐</span>
                  <h3 className={styles.l4eTitle}>{t('communityDetail.news.webListTitle')}</h3>
                  <p className={styles.l4eEmptyMsg}>{t('communityDetail.news.webListEmpty')}</p>
                </div>
              ) : (
                <div className={styles.itemList}>
                  {webItems.map((item) => {
                    const a = item.article;
                    if (!a) return null;
                    const badge = contentTypeBadge(a.contentType);
                    return (
                      <div
                        key={item.moderationGuid}
                        className={`${styles.curateItem} ${webSelected.has(item.newsItemGuid) ? styles.poolItemSelected : ''}`}
                      >
                        <input
                          type="checkbox"
                          className={styles.poolCheckbox}
                          checked={webSelected.has(item.newsItemGuid)}
                          onChange={() => toggleWebSelection(item.newsItemGuid)}
                        />
                        {a.imageUrl ? (
                          <img className={styles.itemThumb} src={mediaSrc(a.imageUrl) ?? undefined} alt="" loading="lazy" />
                        ) : (
                          <div className={styles.itemThumbPlaceholder}>
                            {a.embedType === 'video' ? '▶️' : '🔗'}
                          </div>
                        )}
                        <div className={styles.itemContent}>
                          <div className={styles.itemSource}>
                            <span className={badge.cls}>{badge.label}</span>
                            {a.sourceName || a.sourceDomain || a.ogSiteName || 'Web'}
                          </div>
                          <h4 className={styles.itemTitle}>{a.title}</h4>
                          {a.shareComment && (
                            <div className={styles.itemExcerpt}>"{a.shareComment}"</div>
                          )}
                          <div className={styles.itemMeta}>
                            <span>{formatDate(a.publishedAt)}</span>
                            {a.canonicalUrl && (
                              <span className={styles.webItemUrl}>
                                {new URL(a.canonicalUrl).hostname}
                              </span>
                            )}
                          </div>
                        </div>
                        <button
                          className={styles.webDeleteBtn}
                          onClick={() => handleRemoveWebItem(item.moderationGuid)}
                          disabled={acting === item.moderationGuid}
                          aria-label={t('communityDetail.news.webDeleteBtn')}
                          title={t('communityDetail.news.webDeleteBtn')}
                        >
                          {acting === item.moderationGuid ? '…' : '✕'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer — jump to curate tab to manage placement */}
            {webSelected.size > 0 && (
              <div className={styles.configFooter}>
                <span className={styles.fetchResultMsg}>
                  {t('communityDetail.news.l4eSelectedCount').replace('{{count}}', String(webSelected.size))}
                </span>
                <div className={styles.configActions}>
                  <button
                    className={styles.fetchBtn}
                    onClick={handleViewInCurate}
                  >
                    {t('communityDetail.news.l4eViewInCurate')}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ────────────────────────────────────────────────── */}
        {/* PREVIEW & PUBLISH TAB                              */}
        {/* ────────────────────────────────────────────────── */}
        {tab === 'preview' && (() => {
          const hasContent = approvedItems.length > 0;

          return (
            <div className={styles.body}>
              {curateLoading ? (
                <div className={styles.emptyState}>{t('communityDetail.news.loading')}</div>
              ) : !hasContent ? (
                <div className={styles.emptyState}>
                  {t('communityDetail.news.previewEmpty')}
                </div>
              ) : (
                <div className={styles.previewContent}>
                  <p className={styles.previewHint}>
                    {t('communityDetail.news.previewHint')}
                  </p>

                  {/* Featured hero — text left, image right (flips in RTL) */}
                  {previewHero && (
                    <div className={styles.previewHero}>
                      <div className={styles.previewHeroBody}>
                        <span className={styles.previewFeaturedBadge}>
                          {t('communityDetail.news.featured')}
                        </span>
                        <div className={styles.previewHeroSource}>
                          {previewHero.article?.sourceName || previewHero.article?.sourceDomain}
                        </div>
                        <h3 className={styles.previewHeroTitle}>
                          {previewHero.article?.title}
                        </h3>
                        {previewHero.article?.excerpt && (
                          <p className={styles.previewHeroExcerpt}>
                            {previewHero.article.excerpt}
                          </p>
                        )}
                        <div className={styles.previewDate}>
                          {timeSince(previewHero.article?.publishedAt)}
                        </div>
                      </div>
                      {previewHero.article?.imageUrl ? (
                        <img
                          className={styles.previewHeroImage}
                          src={mediaSrc(previewHero.article.imageUrl) ?? undefined}
                          alt=""
                        />
                      ) : (
                        <div className={styles.previewHeroImagePlaceholder}>📰</div>
                      )}
                    </div>
                  )}

                  {/* Mini cards row — drag to reorder */}
                  {previewCards.length > 0 && (
                    <>
                      <div className={styles.previewSectionHeader}>
                        <span className={styles.previewSectionLabel}>
                          {t('communityDetail.news.placementCard')}
                        </span>
                        {previewCards.length > 1 && (
                          <span className={styles.reorderHint}>
                            {t('communityDetail.news.reorderHint')}
                          </span>
                        )}
                      </div>
                      <div className={styles.previewCardsRow}>
                        {previewCards.map((item, idx) => (
                          <div
                            key={item.moderationGuid}
                            className={`${styles.previewCardWrapper} ${draggingList === 'cards' && draggingIndex === idx ? styles.dragging : ''} ${draggingList === 'cards' && dragOverIndex === idx ? styles.dragOver : ''}`}
                            draggable
                            onDragStart={(e) => handleDragStart(e, 'cards', idx)}
                            onDragOver={(e) => handleDragOver(e, 'cards', idx)}
                            onDrop={(e) => handleDrop(e, 'cards', idx)}
                            onDragEnd={handleDragEnd}
                          >
                            {previewCards.length > 1 && (
                              <span className={styles.previewCardDragHandle} title={t('communityDetail.news.dragToReorder')}>⠿</span>
                            )}
                            <div className={styles.previewCard}>
                              {item.article?.imageUrl ? (
                                <img
                                  className={styles.previewCardImage}
                                  src={mediaSrc(item.article.imageUrl) ?? undefined}
                                  alt=""
                                />
                              ) : (
                                <div className={styles.previewCardImagePlaceholder}>📄</div>
                              )}
                              <div className={styles.previewCardBody}>
                                <div className={styles.previewCardSource}>
                                  {item.article?.sourceName || item.article?.sourceDomain}
                                </div>
                                <h4 className={styles.previewCardTitle}>{item.article?.title}</h4>
                                <span className={styles.previewDate}>
                                  {timeSince(item.article?.publishedAt)}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}

                  {/* Mini headline list — drag to reorder */}
                  {previewHeadlines.length > 0 && (
                    <div className={styles.previewHeadlines}>
                      <div className={styles.previewHeadlinesTitle}>
                        {t('communityDetail.news.moreStories')}
                        {previewHeadlines.length > 1 && (
                          <span className={styles.reorderHint}>
                            {t('communityDetail.news.reorderHint')}
                          </span>
                        )}
                      </div>
                      {previewHeadlines.map((item, idx) => (
                        <div
                          key={item.moderationGuid}
                          className={`${styles.previewHeadlineItem} ${draggingList === 'headlines' && draggingIndex === idx ? styles.dragging : ''} ${draggingList === 'headlines' && dragOverIndex === idx ? styles.dragOver : ''}`}
                          draggable
                          onDragStart={(e) => handleDragStart(e, 'headlines', idx)}
                          onDragOver={(e) => handleDragOver(e, 'headlines', idx)}
                          onDrop={(e) => handleDrop(e, 'headlines', idx)}
                          onDragEnd={handleDragEnd}
                        >
                          {previewHeadlines.length > 1 && (
                            <span className={styles.dragHandle} title={t('communityDetail.news.dragToReorder')}>⠿</span>
                          )}
                          <div className={styles.previewHeadlineDot} />
                          <div className={styles.previewHeadlineContent}>
                            <span className={styles.previewHeadlineTitle}>
                              {item.article?.title}
                            </span>
                            <span className={styles.previewHeadlineMeta}>
                              {item.article?.sourceName || item.article?.sourceDomain}
                              {' · '}
                              {timeSince(item.article?.publishedAt)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Publish footer */}
              {hasContent && !curateLoading && (
                <div className={styles.configFooter}>
                  <span className={styles.previewSummary}>
                    {t('communityDetail.news.previewSummary')
                      .replace('{{total}}', String(approvedItems.length))
                      .replace('{{featured}}', String(previewHero ? 1 : 0))
                      .replace('{{cards}}', String(previewCards.length))
                      .replace('{{headlines}}', String(previewHeadlines.length))}
                  </span>
                  <div className={styles.configActions}>
                    <button
                      className={styles.fetchBtn}
                      onClick={() => {
                        onChanged?.();
                        onClose();
                      }}
                    >
                      {t('communityDetail.news.publishFeed')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default NewsCurateModal;
